"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OpCausalHistory = void 0;
const HashedMap_1 = require("../model/HashedMap");
const HashedSet_1 = require("../model/HashedSet");
const MutationOp_1 = require("../model/MutationOp");
const Hashing_1 = require("../model/Hashing");
class OpCausalHistory {
    constructor(opOrLiteral, prevOpCausalHistories) {
        var _a;
        if (opOrLiteral instanceof MutationOp_1.MutationOp) {
            const op = opOrLiteral;
            if (prevOpCausalHistories === undefined) {
                throw new Error('Parameter prevOpCausalHistories is mandatory to create an OpCausalHistory from a MutationOp');
            }
            this.opHash = op.hash();
            this.prevOpHistories = new Set();
            for (const prevOpRef of (_a = op.prevOps) === null || _a === void 0 ? void 0 : _a.values()) {
                const history = prevOpCausalHistories.get(prevOpRef.hash);
                if (history === undefined) {
                    throw new Error('Cannot create causal history for op ' + op.hash() + ', causal history for prevOp ' + prevOpRef.hash + ' is missing.');
                }
                const prevOpHistory = history instanceof OpCausalHistory ? history.causalHistoryHash : history;
                this.prevOpHistories.add(prevOpHistory);
            }
            this.opProps = op.getCausalHistoryProps();
            this.causalHistoryHash = this.hash();
        }
        else {
            const literal = opOrLiteral;
            OpCausalHistory.checkLiteralFormat(literal);
            this.causalHistoryHash = literal.causalHistoryHash;
            this.opHash = literal.opHash;
            this.prevOpHistories = new Set(literal.prevOpHistories);
            this.opProps = new Map();
            if (literal.opProps !== undefined) {
                for (const key of Object.keys(literal.opProps)) {
                    this.opProps.set(key, literal.opProps[key]);
                }
            }
        }
    }
    verifyOpMatch(op, prevOpCausalHistories) {
        if (op.hash() !== this.opHash) {
            return false;
        }
        const receivedProps = new HashedMap_1.HashedMap();
        for (const [propName, propVal] of op.getCausalHistoryProps().entries()) {
            receivedProps.set(propName, propVal);
        }
        const expectedProps = new HashedMap_1.HashedMap();
        for (const [propName, propVal] of this.opProps.entries()) {
            expectedProps.set(propName, propVal);
        }
        if (!receivedProps.equals(expectedProps)) {
            return false;
        }
        const receivedHistories = new HashedSet_1.HashedSet();
        for (const prevOpRef of op.getPrevOps()) {
            const prevOpHistory = prevOpCausalHistories.get(prevOpRef.hash);
            if (prevOpHistory === undefined) {
                throw new Error('Cannot verify history for op ' + this.opHash + ', history for prevOp ' + prevOpRef.hash + ' is missing');
            }
            receivedHistories.add(prevOpHistory.causalHistoryHash);
        }
        const expectedHistories = new HashedSet_1.HashedSet(this.prevOpHistories.values());
        if (!receivedHistories.equals(expectedHistories)) {
            return false;
        }
        return true;
    }
    hash() {
        const sortedCausalHistoryHashes = Array.from(this.prevOpHistories.values());
        sortedCausalHistoryHashes.sort();
        const p = {};
        for (const propName of Object.keys(this.opProps)) {
            p[propName] = this.opProps.get(propName);
        }
        return Hashing_1.Hashing.forValue({ opHash: this.opHash, history: sortedCausalHistoryHashes, props: p });
    }
    literalize() {
        const literal = {
            causalHistoryHash: this.causalHistoryHash,
            opHash: this.opHash,
            prevOpHistories: Array.from(this.prevOpHistories)
        };
        if (this.opProps.size > 0) {
            literal.opProps = {};
            for (const [key, val] of this.opProps.entries()) {
                literal.opProps[key] = val;
            }
        }
        return literal;
    }
    static computeProps(prevOpCausalHistories) {
        let height = 1;
        let size = 1;
        let good = true;
        for (const prevOpHistory of prevOpCausalHistories.values()) {
            if (prevOpHistory instanceof OpCausalHistory && prevOpHistory._computedProps !== undefined) {
                if (prevOpHistory._computedProps.height + 1 > height) {
                    height = prevOpHistory._computedProps.height + 1;
                }
                size = size + prevOpHistory._computedProps.size;
            }
            else {
                good = false;
                break;
            }
        }
        if (good) {
            return { height: height, size: size };
        }
        else {
            return undefined;
        }
    }
    static checkLiteralFormat(literal) {
        const propTypes = { causalHistoryHash: 'string', opHash: 'string', prevOpHistories: 'object' };
        for (const propName of ['causalHistoryHash', 'opHash', 'prevOpHistories']) {
            const prop = literal[propName];
            if (prop === undefined) {
                throw new Error('OpCausalHistory literal is missing property: ' + propName);
            }
            if (typeof (prop) !== propTypes[propName]) {
                throw new Error('OpCausalHistory literal property ' + propName + ' has the wrong type, expected ' + propTypes[propName] + ' but found ' + typeof (prop));
            }
        }
        if (!Array.isArray(literal.prevOpHistories)) {
            throw new Error('OpCausalHistory prevOpHistories should be an array');
        }
        for (const hash of literal.prevOpHistories) {
            if (typeof (hash) !== 'string') {
                throw new Error('OpCausalHistory prevOpHistories should contain only strings, found ' + typeof (hash) + ' instead');
            }
        }
        if (literal.opProps !== undefined) {
            if (typeof (literal.opProps) !== 'object') {
                throw new Error('OpCausalHistory literal property opProps has the wrong type, expected object but found ' + typeof (literal.opProps));
            }
            const keys = Object.keys(literal.opProps);
            if (keys.length === 0) {
                throw new Error('OpCausalHistory literal property opProps is empty, it should either be missing altogether or be non-empty.');
            }
            const customPropTypes = ['string', 'number', 'bigint'];
            for (const customPropName of Object.keys(literal.opProps)) {
                if (customPropTypes.indexOf(typeof (literal.opProps[customPropName])) < 0) {
                    throw new Error('Unexpected type found in OpCausalHistory literal opProps: ' + typeof (literal.opProps[customPropName] + ' (expected string, number of bigint)'));
                }
            }
        }
    }
}
exports.OpCausalHistory = OpCausalHistory;
//# sourceMappingURL=OpCausalHistory.js.map